import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, FieldError } from 'react-hook-form';
import { Popover, Portal } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';

interface SelectOption {
  value: string | number;
  label: string;
}

interface FormSelectProps {
  name?: string;
  control?: any;
  label?: string;
  options: SelectOption[];
  placeholder: string;
  disabled?: boolean;
  showLabel?: boolean; // default true
  value?: string | number | undefined; // standalone mode
  onChange?: (value: string | number | undefined) => void; // standalone mode
  required?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  containerClassName?: string;
  triggerClassName?: string;
  panelMinWidth?: number;
}

const FormSelect: React.FC<FormSelectProps> = ({
  name,
  control,
  label,
  options,
  placeholder,
  disabled,
  showLabel = true,
  value,
  required = false,
  onChange,
  searchable = false,
  searchPlaceholder = 'Search...',
  containerClassName,
  triggerClassName,
  panelMinWidth
}) => {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [panelStyle, setPanelStyle] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 0 });
  const [openUp, setOpenUp] = useState<boolean>(false);
  const ESTIMATED_PANEL_HEIGHT = 300; // Approximate for max-h-64 (256px) + search bar + borders/margins
  const MARGIN = 8;
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [panelHeight, setPanelHeight] = useState<number>(
    ESTIMATED_PANEL_HEIGHT
  );

  const updatePosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const measuredHeight =
      panelRef.current?.offsetHeight ?? panelHeight ?? ESTIMATED_PANEL_HEIGHT;
    // For fixed positioning, coordinates are relative to the viewport
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    // Open upwards if insufficient space below but enough above
    const shouldOpenUp =
      spaceBelow < measuredHeight + MARGIN &&
      spaceAbove >= measuredHeight + MARGIN;
    setOpenUp(shouldOpenUp);

    let top: number;

    if (shouldOpenUp) {
      top = Math.max(MARGIN, rect.top - measuredHeight - MARGIN);
    } else {
      top = rect.bottom + MARGIN;
      const maxTop = Math.max(
        MARGIN,
        window.innerHeight - measuredHeight - MARGIN
      );
      if (top > maxTop) {
        top = maxTop;
      }
    }
    const desiredWidth =
      panelMinWidth !== undefined
        ? Math.max(rect.width, panelMinWidth)
        : rect.width;
    let left = rect.left;
    if (left + desiredWidth > window.innerWidth - MARGIN) {
      left = Math.max(MARGIN, window.innerWidth - desiredWidth - MARGIN);
    }
    setPanelStyle({ top, left, width: desiredWidth });
  }, [panelHeight, panelMinWidth]);

  const setPanelNode = useCallback(
    (node: HTMLDivElement | null) => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      panelRef.current = node;

      if (!node) {
        return;
      }

      const measureAndUpdate = () => {
        const nextHeight = node.offsetHeight;
        setPanelHeight(prev =>
          Math.abs(prev - nextHeight) > 1 ? nextHeight : prev
        );
        requestAnimationFrame(() => updatePosition());
      };

      measureAndUpdate();

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserverRef.current = new ResizeObserver(entries => {
          const entry = entries[0];
          if (!entry) return;
          const nextHeight = entry.contentRect.height;
          setPanelHeight(prev =>
            Math.abs(prev - nextHeight) > 1 ? nextHeight : prev
          );
          requestAnimationFrame(() => updatePosition());
        });

        resizeObserverRef.current.observe(node);
      }
    },
    [updatePosition]
  );

  useEffect(() => {
    updatePosition();
    const handler = () => {
      requestAnimationFrame(() => updatePosition());
    };
    window.addEventListener('resize', handler, { passive: true });
    window.addEventListener('scroll', handler, {
      capture: true,
      passive: true
    });
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, { capture: true });
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [updatePosition]);

  const getFilteredOptions = () => {
    return searchable
      ? options.filter(o =>
          o.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : options;
  };

  const renderDropdownContent = (
    close: () => void,
    isSelectedFn: (option: SelectOption) => boolean,
    onSelect: (option: SelectOption) => void
  ) => {
    const filteredOptions = getFilteredOptions();
    return (
      <div
        className={`bg-white border border-gray-200 rounded-md shadow-lg ${openUp ? 'origin-bottom' : 'origin-top'}`}
      >
        {searchable && (
          <div className='p-2 border-b border-gray-200 bg-white sticky top-0'>
            <input
              type='text'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className='w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>
        )}
        <ul className='py-1 text-sm text-slate-900 max-h-64 overflow-auto'>
          {filteredOptions.length === 0 && (
            <li className='px-2 py-1.5 text-slate-500'>No results</li>
          )}
          {filteredOptions.map(option => {
            const isSelected = isSelectedFn(option);
            return (
              <li
                key={option.value.toString()}
                className={`px-2 py-1.5 cursor-pointer hover:bg-blue-50 truncate ${
                  isSelected ? 'bg-blue-100' : ''
                }`}
                title={option.label}
                onClick={() => {
                  onSelect(option);
                  close();
                }}
              >
                {option.label}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div className={containerClassName}>
      {showLabel && label && (
        <label className='block text-sm font-medium text-slate-700 mb-1'>
          {label} {required && <span className='text-red-500'>*</span>}
        </label>
      )}

      {control ? (
        <Controller
          name={name as string}
          control={control}
          render={({ field, formState: { errors } }) => {
            const error = errors[name as string] as FieldError | undefined;
            if (!options?.length) {
              return (
                <div className='border rounded px-2 py-1.5 w-full text-gray-400'>
                  {placeholder}
                </div>
              );
            }
            return (
              <>
                <Popover className='relative w-full'>
                  {({ close }) => (
                    <>
                      <Popover.Button
                        as='div'
                        // HeadlessUI types Popover.Button as button by default; cast to any to support div
                        ref={buttonRef as unknown as React.Ref<any>}
                        onClick={() => {
                          updatePosition();
                          if (searchable) setSearchQuery('');
                        }}
                        className='w-full cursor-pointer'
                      >
                        <div
                          className={`border rounded px-2 py-1.5 w-full flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            disabled
                              ? 'bg-gray-100 text-gray-500 border-gray-300'
                              : `bg-white ${error ? 'border-red-500' : 'border-gray-300'}`
                          } ${triggerClassName ?? ''}`}
                          aria-disabled={disabled}
                        >
                          <span
                            className={`truncate flex-1 min-w-0 text-left ${field.value === undefined || field.value === null || field.value === '' ? 'text-gray-400' : 'text-slate-900'}`}
                            title={
                              options.find(o => o.value === field.value)
                                ?.label || placeholder
                            }
                          >
                            {options.find(o => o.value === field.value)
                              ?.label || placeholder}
                          </span>
                          <ChevronDown className='h-4 w-4 text-slate-500 ml-2 shrink-0' />
                        </div>
                      </Popover.Button>

                      {!disabled && (
                        <Portal>
                          <Popover.Panel
                            ref={setPanelNode}
                            className='fixed z-[1000]'
                            style={{
                              top: panelStyle.top,
                              left: panelStyle.left,
                              width: panelStyle.width
                            }}
                          >
                            {renderDropdownContent(
                              close,
                              option => option.value === field.value,
                              option => field.onChange(option.value)
                            )}
                          </Popover.Panel>
                        </Portal>
                      )}
                    </>
                  )}
                </Popover>
                {error && (
                  <p className='text-red-500 text-xs mt-1'>{error.message}</p>
                )}
              </>
            );
          }}
        />
      ) : (
        // Standalone mode without react-hook-form
        <Popover className='relative w-full'>
          {({ close }) => (
            <>
              <Popover.Button
                as='div'
                ref={buttonRef as unknown as React.Ref<any>}
                onClick={() => {
                  updatePosition();
                  if (searchable) setSearchQuery('');
                }}
                className='w-full cursor-pointer'
              >
                <div
                  className={`border rounded px-2 py-1.5 w-full flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    disabled
                      ? 'bg-gray-100 text-gray-500 border-gray-300'
                      : 'bg-white border-gray-300'
                  } ${triggerClassName ?? ''}`}
                  aria-disabled={disabled}
                >
                  <span
                    className={`truncate flex-1 min-w-0 text-left ${value === undefined || value === null || value === '' ? 'text-gray-400' : 'text-slate-900'}`}
                    title={
                      options.find(o => o.value === value)?.label || placeholder
                    }
                  >
                    {options.find(o => o.value === value)?.label || placeholder}
                  </span>
                  <ChevronDown className='h-4 w-4 text-slate-500 ml-2 shrink-0' />
                </div>
              </Popover.Button>

              {!disabled && (
                <Portal>
                  <Popover.Panel
                    ref={setPanelNode}
                    className='fixed z-[1000]'
                    style={{
                      top: panelStyle.top,
                      left: panelStyle.left,
                      width: panelStyle.width
                    }}
                  >
                    {renderDropdownContent(
                      close,
                      option => option.value === value,
                      option => onChange?.(option.value)
                    )}
                  </Popover.Panel>
                </Portal>
              )}
            </>
          )}
        </Popover>
      )}
    </div>
  );
};

export default FormSelect;
